/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import React, { forwardRef, useEffect, useRef, useState } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import { convertDate } from "@Services/luxonHelper";
import "./issues.css";
import IssueDetails from "./IssueDetails/IssueDetails";
import { useNavigate, useParams } from "react-router-dom";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_WARNING } from "@Services/constants";
import { getIssueViewerUrl, isIssueOpenableInViewer } from "../../utils/issueViewerNavigation";
import CreateIssueModal from "./CreateIssueModal";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import { setEditIssuesData } from "@Features/issues/state/editIssues";
import { deleteIssueData } from "@Features/issues/state/deletingIssues.actions";
import { deleteIssuesPinData } from "@Features/issues/state/issuesPinDataSlice";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import CreateIcon from "@Components/StickySidebarViewer/assets/CreateIcon";
import { getIssuesUrl } from "@Features/projects/api/projects.api";
import GridPageLayout from "@CubeComponents/html/GridPageLayout";
import { useTranslation } from "@i18n";
import type { TranslatedString } from "@i18n";
import SlickGrid1, {
  type QueryShape,
  type LayoutData,
  type GridClickEvent,
  type GridRowBase,
  type ColumnDefinition,
  type ValidatedColumnDefinition,
  type SlickGrid1Ref,
  type ToolbarItem,
} from "@CubeComponents/html/SlickGrid1";
import { HeaderWithIcon } from "@CubeComponents/html/SlickGrid1/Formatters/Formatters";
import Text from "@CubeComponents/html/Text/Text";
import { renderToStaticMarkup } from "react-dom/server";
import { getUrlForImage } from "@CubeComponents/constants/globalFunctions";
import { getImageUrl } from "@Utils";
import { isIssueRequiredPermission, fetchIssueById } from "@Features/issues/state/bcfData.actions";
import IssueStatusPopover from "../IssueStatusPopover/IssueStatusPopover";
import { store } from "../../../../ProviderApp";
import { RootState, AppDispatch } from "@Reducers/index";
import { folderPermissions } from "@Helpers/constants";
import { EXPORT_TYPE, exportPdf } from "@Services/fileDownload";
import ExportPdfOptionsModal from "@CubeComponents/form/PaperSizeSelector/ExportPdfOptionsModal";
import type { PaperSize } from "@CubeComponents/form/PaperSizeSelector/PaperSizeSelector";
import { sanitizeHtml } from "@CubeComponents/html/SlickGrid1/slickGridUtils";
import type { IssueItem } from "@ServerTypes/issues/issues.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import { createRoot, type Root } from "react-dom/client";

const { RESOURCE_DOWNLOAD, RESOURCE_COMMENT, RESOURCE_MODIFY, RESOURCE_DELETE } = folderPermissions();
const STATUS_ROOT_LIMIT = 100;

/**
 * A grid row. Rows arrive from `getIssuesData` through the remote data source, so they are
 * `IssueItem`s plus SlickGrid's own bookkeeping (`__isDummy` marks the placeholder rows the
 * infinite scroller inserts while a page is in flight). The `Record<string, unknown>` arm carries
 * the flattened `customFields.<questionId>` columns built at runtime from each issue's form
 * template, and gives the type the implicit index signature `ColumnDefinition<Row, string>` needs —
 * several columns bind a dotted path (`statusInReview.name`) rather than a plain key.
 */
type IssueGridRow = IssueItem & GridRowBase & Record<string, unknown>;

/**
 * A column as this grid builds them. `ColumnDefinition` covers everything except: `frozen` (the
 * SlickGrid flag; the interface declares only `isFrozen`), `hide` (read by the `.filter` below and
 * never forwarded), and the `isCustomField` / `headerText` pair carried by the form-template
 * columns assembled at runtime.
 */
type IssueGridColumn = ColumnDefinition<IssueGridRow, string> & {
  frozen?: boolean;
  hide?: boolean;
  isCustomField?: boolean;
  headerText?: string;
};

/**
 * The grid's `dataView` plus the `items` array this component reads. `items` is `protected` on the
 * shipped `SlickDataView`, so it is reached through an erased intersection rather than the class.
 */
type DataViewWithItems = SlickGrid1Ref<IssueGridRow>["dataView"] & { items: IssueGridRow[] };

/** `ToolbarItem` declares `text`, not `name`, and has no `buttonType`; this grid uses both. */
type IssuesToolbarItem = ToolbarItem & {
  name?: string;
  buttonType?: string;
};

interface IssuesDataProps {
  showTitleLine: boolean;
  issuesControlPaneClass: string;
  columnDefinitions: IssueGridColumn[];
  toolbarOptions: IssuesToolbarItem[];
  remoteDataSource: string;
  queryParams: QueryShape;
  projectId: string;
  enableWrapText: boolean;
  setEnableWrapText: (value: boolean) => void;
  toolbarClickHandler: (cmd: string) => void;
  onClickLayout: (params: LayoutData) => void;
  onSelectedRowIdsChanged: () => void;
  openIssuesDetails: boolean;
  closeIssueSidebar: () => void;
  sidebardata: IssueItem[];
  disabledIssueIds?: string[];
  disabledViews?: boolean;
  allowOpenSidebar?: boolean;
  allowTools?: boolean;
  refreshDataGrid?: () => void;
  updateGridRowData?: (id: string, updates: Record<string, unknown>) => void;
  outerClassName?: string;
  onClick?: (args: GridClickEvent) => void;
}
const IssuesData = forwardRef<SlickGrid1Ref<IssueGridRow>, IssuesDataProps>(
  (
    {
      showTitleLine,
      issuesControlPaneClass,
      columnDefinitions,
      toolbarOptions,
      remoteDataSource,
      queryParams,
      projectId,
      enableWrapText,
      setEnableWrapText,
      toolbarClickHandler,
      onClickLayout,
      onSelectedRowIdsChanged,
      sidebardata,
      closeIssueSidebar,
      openIssuesDetails,
      disabledIssueIds,
      disabledViews,
      allowOpenSidebar,
      allowTools = true,
      refreshDataGrid: _refreshDataGrid,
      updateGridRowData,
      outerClassName = "",
      onClick,
    },
    ref,
  ) => {
    return (
      <GridPageLayout
        showHeader={showTitleLine}
        icon="issues"
        title="Issues"
        description="List of all the issues created in this project"
        outerClassName={outerClassName}
        gridContainerClassName={`${issuesControlPaneClass} issuesControlPane`}
      >
        <SlickGrid1
          ref={ref}
          // `ValidatedColumnDefinition` carries a brand only `typedColumns()` can mint; these are
          // plain object literals assembled in state, so the brand is asserted at the boundary.
          columnDefinitions={columnDefinitions as typeof columnDefinitions & ValidatedColumnDefinition<IssueGridRow>[]}
          rowHeight={96}
          gridOptions={{
            headerRowHeight: 200,
            showPreHeaderPanel: true,
            preHeaderPanelHeight: 30,
            topHeaderPanelHeight: 30,
            enableAutoSizeColumns: false,
          }}
          // `allowTools && …` is `false` when tools are off; the grid declares the prop as an array
          // and reads `disableToolbar` below for the off case. Erased assertion.
          toolbar={(allowTools && toolbarOptions) as IssuesToolbarItem[]}
          enableRowResize
          remoteDataSource={remoteDataSource}
          query={queryParams}
          {...(onClick && { onClick })}
          enableSelectAllCheckbox
          enableSearchBar
          disableToolbar={!allowTools}
          enableViews={!disabledViews && Boolean(projectId)}
          onSelectedRowIdsChanged={onSelectedRowIdsChanged}
          {...(!disabledViews &&
            Boolean(projectId) && {
              viewsOptions: {
                headerName: "Views",
                name: "View",
                resourceType: "ISSUES",
                selectedLayoutKey: "ISSUES_SELECTED_LAYOUT",
                fetchParams: {
                  projectId,
                },
              },
            })}
          toolbarClickHandler={toolbarClickHandler}
          excelExport
          setEnableWrapText={setEnableWrapText}
          enableFilteringOption
          enableWrapText={enableWrapText}
          onClickLayout={onClickLayout}
          enableSortingOption
          enableGroupingOption
          propertiesPanel={allowOpenSidebar}
          enableColumnChooser
          disabledIds={disabledIssueIds}
        />
        {openIssuesDetails && (
          <IssueDetails
            allowCloseBtn={false}
            closePropertiesCallback={closeIssueSidebar}
            rowData={sidebardata}
            updateGridRowData={updateGridRowData}
          />
        )}
      </GridPageLayout>
    );
  },
);

IssuesData.displayName = "IssuesData";

interface IssuesProps {
  /** Truthy flag (callers pass either a bare boolean or a spaceId string). */
  getAllIssuesInSpace?: string | boolean;
  showTitleLine?: boolean;
  allowTools?: boolean;
  /** File version ids to scope the list by; may be null when unscoped. */
  fileVersionIds?: string[] | null;
  /** Truthy flag carrying a spaceId string when standalone issues are requested. */
  getStandaloneIssuesForSpaceId?: string;
  spaceId?: string;
  issueSelectionCallback?: (selectedRecords: IssueItem[]) => void;
  showStatusCol?: boolean;
  disabledIssueIds?: string[];
  allowOpenSidebar?: boolean;
  disabledViews?: boolean;
  /** Truthy flag; routed page passes the raw `byPortal` search-param string. */
  isPortalList?: string | boolean;
  /** Forwarded by callers but not consumed here (grid selection mode). */
  showCheckboxes?: boolean;
  /**
   * Take Action dashboard mode: shows the issues assigned to the logged-in user across all their
   * projects, bucketed by `activeFilter`. Renames the Due Date column to "Next Action Due" with the
   * approaching/exceeded badge, and makes a row click open the issue in the viewer (or warn when standalone).
   */
  isActionable?: boolean;
  /** Due-date bucket for actionable mode: "overdue" | "next7days" | "next30days" | null (List View). */
  activeFilter?: string | null;
  /** Restrict the actionable list/counts to a chosen set of projects (empty = all the user's projects). */
  selectedProjectIds?: string[];
  /** Take Action only: called after a status change that reloads the grid, so the parent can refresh its module counts. */
  onActionableChange?: () => void;
}

function Issues({
  getAllIssuesInSpace,
  showTitleLine = true,
  allowTools = true,
  fileVersionIds = [],
  getStandaloneIssuesForSpaceId,
  spaceId,
  issueSelectionCallback,
  showStatusCol = true,
  disabledIssueIds,
  allowOpenSidebar = true,
  disabledViews = false,
  isPortalList,
  isActionable = false,
  activeFilter = null,
  selectedProjectIds,
  onActionableChange,
}: IssuesProps) {
  const { t } = useTranslation();
  const [openIssuesDetails, setOpenIssuesDetails] = useState(false);
  const [showExportPdfOptions, setShowExportPdfOptions] = useState(false);
  const [sidebardata, setSidebardata] = useState<IssueItem[]>([]);
  const [openCreateIssue, setOpenCreateIssue] = useState(false);
  const [selectedIssues, setSelectedIssues] = useState<IssueItem[]>([]);
  const [editIssue, setEditIssue] = useState<IssueItem | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const dataGrid = useRef<SlickGrid1Ref<IssueGridRow> | null>(null);

  const statusRootsRef = useRef<Map<string, { container: HTMLElement; root: Root }>>(new Map());
  useEffect(() => {
    const registry = statusRootsRef.current;
    return () => {
      const roots = [...registry.values()];
      registry.clear();
      queueMicrotask(() => roots.forEach(({ root }) => root.unmount()));
    };
  }, []);
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [enableWrapText, setEnableWrapText] = useState(false);
  const [pdfExportEnabled, setPdfExportEnabled] = useState(false);

  const userDetails = useSelector((state: RootState) => state.userDetails);

  const projectId = params.projectId || "";

  const severityTypes: Record<string, string> = {
    MINOR: t("Minor"),
    MAJOR: t("Major"),
  };

  const toolbarClickHandler1 = async (args: string) => {
    if (args === "custom_createIssue") {
      setOpenCreateIssue(true);
    }
    if (args === "editIssue") {
      handleEditIssue();
    } else if (args === "deleteIssue") {
      setShowDeleteConfirm(true);
    }
    if (args === "properties") {
      setOpenIssuesDetails((prev) => !prev);
      // KNOWN BUG, preserved verbatim: the optional chain yields `undefined` before the grid
      // mounts, which is then stored as the sidebar's row array. Erased assertion.
      setSidebardata(dataGrid.current?.dataView.getAllSelectedItems()!);
    } else if (args == "PdfExport") {
      handleExportToPdf();
    }
  };

  const handleEditIssue = async () => {
    if (selectedIssues.length !== 1) return;
    const selected = selectedIssues[0];
    if (!selected) return;
    let fullIssue: IssueItem | undefined;
    try {
      fullIssue = await dispatch(fetchIssueById(selected.id));
    } catch {
      fullIssue = undefined;
    }
    if (!fullIssue) {
      dispatch(showToastNotification({ content: SOMETHING_WENT_WRONG, type: TOAST_TYPE_ERROR }));
      return;
    }
    dispatch(setEditIssuesData(fullIssue));
    setEditIssue(fullIssue);
  };

  const handleModalClose = () => {
    if (editIssue) {
      setEditIssue(null);
      dispatch(setEditIssuesData(null));
    } else {
      setOpenCreateIssue(false);
    }
  };

  const clearGridSelection = () => {
    dataGrid.current?.slickGrid?.setSelectedRows?.([]);
    dataGrid.current?.dataView?.setSelectedIds?.([], { isRowBeingAdded: true });
    setSelectedIssues([]);
    setSidebardata([]);
  };

  const handleConfirmDelete = async () => {
    const ids = selectedIssues.map((record) => record.id);
    if (!ids.length) return;
    setIsDeleting(true);

    const deleted = await dispatch(deleteIssueData(ids));
    setIsDeleting(false);
    setShowDeleteConfirm(false);
    if (deleted) {
      dispatch(deleteIssuesPinData(ids));
      clearGridSelection();
      refreshDataGrid();
    }
  };

  const createBtnTemplate = () => (
    <ButtonComponent
      id="custom_createMail"
      buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
      onClick={() => toolbarClickHandler1("custom_createIssue")}
      disabled={!allowTools}
    >
      <Text>
        {" "}
        <Text className="flex items-center">
          <CreateIcon size="15" className="me-2" />
          {t("Create")}
        </Text>
      </Text>
    </ButtonComponent>
  );

  function closeIssueSidebar() {
    setOpenIssuesDetails(false);
  }

  const refreshDataGrid = () => {
    if (dataGrid.current?.refresh) {
      dataGrid.current.refresh();
    }
  };

  const updateGridRowData = (id: string, updates: Record<string, unknown>) => {
    const dataView = dataGrid.current?.dataView;
    if (!dataView) return;
    const item = dataView.getItemById(id);
    if (item) {
      dataView.updateItem(id, { ...item, ...updates });
    }
  };

  // Editable status cell for the standalone Issues list only — not the portal list, not the Take Action
  // dashboard, and not the reviews-embedded grid (which hides the status column via showStatusCol=false).
  const editableStatus = showStatusCol && !isPortalList;

  function editableStatusFormatter(
    _row: number,
    _cell: number,
    _value: unknown,
    _colDef: unknown,
    dataContext: IssueItem,
  ) {
    const container = document.createElement("div");
    container.style.display = "inline-block";
    container.style.width = "100%";

    const issue = {
      // The "" fallback is the pre-existing missing-id sentinel, not an ObjectId, so the whole
      // expression is asserted once here at the declaration.
      id: (dataContext.id ?? "") as ObjectIdString,
      spaceId: dataContext.spaceId ?? "",
      fileVersionId: dataContext.fileVersionId,
      statusInReview: dataContext.statusInReview,
      reviews: dataContext.reviews,
      canEditDefaultStatus: isIssueRequiredPermission(dataContext, RESOURCE_COMMENT.id),
    };

    // Cells are detached React roots (no app providers), so wrap the self-contained popover in the
    // redux store Provider; i18n/MUI theme resolve via their global instances.
    const root = createRoot(container);
    root.render(
      <Provider store={store}>
        <IssueStatusPopover
          issue={issue}
          canEdit={editableStatus}
          fullWidth
          onStatusChanged={(id, statusInReview) => {
            // Take Action lists only issues that still need action; a status change can resolve an
            // issue (all reviews Closed/Revoked), so reload from the server (which reapplies the
            // `isActionable` filter) and refresh the parent's module counts. Elsewhere patch the row.
            if (isActionable) {
              refreshDataGrid();
              onActionableChange?.();
            } else {
              updateGridRowData(id, { statusInReview });
            }
          }}
        />
      </Provider>,
    );

    const registry = statusRootsRef.current;
    const rowId = issue.id;
    const previous = registry.get(rowId);
    if (previous) {
      const staleRoot = previous.root;
      queueMicrotask(() => staleRoot.unmount());
      registry.delete(rowId);
    }
    registry.set(rowId, { container, root });
    while (registry.size > STATUS_ROOT_LIMIT) {
      const oldestKey = registry.keys().next().value;
      if (oldestKey === undefined) break;
      const oldest = registry.get(oldestKey);
      registry.delete(oldestKey);
      if (oldest) {
        const oldestRoot = oldest.root;
        queueMicrotask(() => oldestRoot.unmount());
      }
    }

    return container;
  }

  function createSeverityFormatter(
    _row: number,
    _cell: number,
    _value: unknown,
    _colDef: unknown,
    dataContext: IssueGridRow,
  ) {
    const container = document.createElement("div");
    // [plain-DOM] no React container → zero React event-delegation listeners; native title
    if (dataContext?.severity) {
      container.style.width = "fit-content";
      container.textContent = String(severityTypes[dataContext.severity] ?? dataContext.severity);
      container.setAttribute("data-cube-tip", String(dataContext.severity));
    } else {
      container.textContent = "-";
    }
    return container;
  }

  function officialResponseFormatter(
    _row: number,
    _cell: number,
    _value: unknown,
    _colDef: unknown,
    data: IssueGridRow,
  ) {
    const container = document.createElement("div");

    if (data.officialComment?._id) {
      const doc = new DOMParser().parseFromString(data.officialComment.title || "", "text/html");
      doc.body.querySelectorAll("img").forEach((img: HTMLImageElement) => {
        const textNode = document.createTextNode(" [Image] ");
        img.replaceWith(textNode);
      });

      const text = doc.body.textContent?.trim() || "-";
      container.textContent = text;
    } else {
      container.textContent = "-";
    }

    return container;
  }

  // Take Action "Next Action Due" cell: shows the due date with an approaching/exceeded badge.
  // Mirrors the Reviews dashboard nextActionDueDateTemplate (ReviewsList.tsx), keyed on `dueDate`.
  const nextActionDueDateTemplate = (issue: IssueItem) => {
    if (!issue.dueDate) return "-";

    const dueDateTimestamp = Number(issue.dueDate);
    const currentTimestamp = Date.now();
    const msInOneDay = 1000 * 60 * 60 * 24;

    const daysDifference = Math.ceil((dueDateTimestamp - currentTimestamp) / msInOneDay);

    const isOverdue = daysDifference < 0 || Object.is(daysDifference, -0);
    // Issues flag the row as "approaching" within 7 days, matching the dashboard's 7-day badge window.
    const isDueSoon = daysDifference >= 0 && daysDifference <= 7;

    const formattedDate = convertDate(dueDateTimestamp, "dateForText");

    let badgeColor = "";
    let tooltipMessage: TranslatedString | "" = "";

    if (isOverdue) {
      badgeColor = "bg-red-500";
      tooltipMessage = t("Due date exceeded by {{days}} day(s)", { days: Math.abs(daysDifference) });
    } else if (isDueSoon) {
      badgeColor = "bg-yellow-400";
      tooltipMessage = t("Due date approaching in {{days}} day(s)", { days: daysDifference });
    }

    const container = document.createElement("div");
    container.className = "inline-flex";
    // [plain-DOM] no React container → zero React event-delegation listeners; native title
    const inner = document.createElement("div");
    inner.className = "flex items-center gap-2";
    const dateSpan = document.createElement("span");
    dateSpan.textContent = formattedDate;
    inner.appendChild(dateSpan);
    if (tooltipMessage) {
      const badge = document.createElement("span");
      badge.className = `w-5 h-5 flex items-center justify-center rounded-full text-white text-xs ${badgeColor}`;
      badge.textContent = "!";
      badge.setAttribute("data-cube-tip", String(tooltipMessage));
      inner.appendChild(badge);
    }
    container.appendChild(inner);
    return container;
  };

  // Take Action row click: open the issue in the viewer, or warn when it is a standalone issue.
  // Reuses the same navigation logic as Issue Details → "View in Viewer" (getIssueViewerUrl).
  const recordClick = (args: GridClickEvent) => {
    if (args.detail.args.cell === 0) {
      return;
    }
    const clickedRow: IssueItem | undefined = dataGrid.current?.slickGrid.getDataItem(args.detail.args.row);
    if (!clickedRow) return;

    if (!isIssueOpenableInViewer(clickedRow)) {
      dispatch(
        showToastNotification({
          content: t("This is a standalone Issue."),
          type: TOAST_TYPE_WARNING,
        }),
      );
      return;
    }

    const location = getIssueViewerUrl(clickedRow);
    if (location) {
      navigate(location);
    }
  };

  const canEditSelectedIssue =
    selectedIssues.length === 1 && isIssueRequiredPermission(selectedIssues[0], RESOURCE_MODIFY.id);
  const canDeleteSelectedIssues =
    selectedIssues.length > 0 &&
    selectedIssues.every((record) => isIssueRequiredPermission(record, RESOURCE_DELETE.id));

  const isProjectIssuesList = !isPortalList && !isActionable && Boolean(params.projectId) && !issueSelectionCallback;

  const toolbarOptions1: IssuesToolbarItem[] = [
    ...(isProjectIssuesList
      ? [
          {
            template: createBtnTemplate,
            id: "custom_createIssue",
          },
          {
            id: "editIssue",
            name: t("Edit"),
            prefixIcon: "edit",
            disabled: !canEditSelectedIssue,
          },
          {
            id: "deleteIssue",
            name: t("Delete"),
            prefixIcon: "delete",
            disabled: !canDeleteSelectedIssues,
          },
          {
            id: "PdfExport",
            name: "Export",
            prefixIcon: "PdfExport",
            buttonType: BUTTON_TYPES.TEXT_BUTTON,
            disabled: !pdfExportEnabled,
          },
        ]
      : []),
  ];
  const imageHeaderRenderer = () => renderToStaticMarkup(<HeaderWithIcon icon="Image2" label={t("Image")} />);
  const issueIdHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="ID" label={t("Issue ID")} css="invert-0 brightness-0" />);
  const titleHeaderRenderer = () => renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={t("Title")} />);
  const descriptionHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="Multi Line" label={t("Description")} />);
  const statusHeaderRenderer = () => renderToStaticMarkup(<HeaderWithIcon icon="Dropdown" label={t("Status")} />);
  const projectIdHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="ID" label={t("Project ID")} css="invert-0 brightness-0" />);
  const projectNameHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={t("Project Name")} />);
  const spaceNameHeaderRenderer = () => renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={t("Space Name")} />);
  const fileHeaderRenderer = () => renderToStaticMarkup(<HeaderWithIcon icon="files" label={t("File")} />);
  const fileVersionHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="Version" label={t("File Version")} />);
  const assignedToHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="User Profile" label={t("Assigned to")} />);
  const createdByHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="User Profile" label={t("Created by")} />);
  const createdOnHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="Date and Time" label={t("Created On")} />);
  const dueDateHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="Date and Time" label={t("Due Date")} />);
  const severityHeaderRenderer = () => renderToStaticMarkup(<HeaderWithIcon icon="Dropdown" label={t("Severity")} />);
  const officialResponseHeaderRenderer = () =>
    renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={t("Official Response")} />);
  const [columnDefinitions, setColumnDefinitions] = useState<IssueGridColumn[]>(
    (
      [
        {
          id: "snapshot",
          field: "snapshot",
          name: t("Image"),
          sortable: false,
          filterable: true,
          originalWidth: 106,
          width: 106,
          formatterType: "image",
          frozen: true,
          _valueGetter: (args: IssueGridRow) => args.id,
          _imageGetter: (args: IssueGridRow) => {
            let url = `${getImageUrl()}/Issues_default_image.svg`;
            if (args.snapshot) {
              url = getUrlForImage({ id: args.id }, "IssueImage");
            }
            return url;
          },
          disableSorting: true,
          disableGrouping: true,
          disableFiltering: true,
          headerRenderer: imageHeaderRenderer,
        },
        {
          id: "issueCounter",
          field: "issueCounter",
          name: t("Issue ID"),
          originalWidth: 180,
          width: 180,
          formatterType: "ID",
          filterable: true,
          clipMode: "EllipsisWithTooltip",
          frozen: true,
          headerRenderer: issueIdHeaderRenderer,
        },
        {
          id: "title",
          field: "title",
          name: t("Title"),
          originalWidth: 180,
          width: 180,
          clipMode: "EllipsisWithTooltip",
          headerRenderer: titleHeaderRenderer,
        },
        {
          id: "description",
          field: "description",
          name: t("Description"),
          sortable: false,
          filterable: false,
          originalWidth: 180,
          width: 180,
          formatterType: "description",
          clipMode: "EllipsisWithTooltip",
          disableGrouping: true,
          disableFiltering: true,
          disableSorting: true,
          headerRenderer: descriptionHeaderRenderer,
          _excelValueGetter: (args: IssueGridRow) => (args.description ? sanitizeHtml(args.description) : "-"),
        },
        showStatusCol
          ? {
              id: "status",
              field: "statusInReview.name",
              name: t("Status"),
              originalWidth: 180,
              width: 180,
              formatter: editableStatusFormatter,
              filterable: true,
              filterType: "Text",
              headerRenderer: statusHeaderRenderer,
            }
          : null,
        {
          id: "projectId",
          field: "projectId",
          name: t("Project ID"),
          originalWidth: 180,
          width: 180,
          hide: !isPortalList,
          filterType: "Text",
          clipMode: "EllipsisWithTooltip",
          headerRenderer: projectIdHeaderRenderer,
        },
        {
          id: "projectName",
          field: "projectName",
          name: t("Project Name"),
          originalWidth: 180,
          width: 180,
          hide: !isPortalList,
          filterType: "Text",
          clipMode: "EllipsisWithTooltip",
          headerRenderer: projectNameHeaderRenderer,
        },
        {
          id: "spaceName",
          field: "spaceName",
          name: t("Space Name"),
          originalWidth: 180,
          width: 180,
          filterable: true,
          filterType: "Text",
          headerRenderer: spaceNameHeaderRenderer,
        },
        {
          id: "file",
          field: "file",
          name: t("File"),
          originalWidth: 180,
          width: 180,
          filterable: true,
          filterType: "Text",
          clipMode: "EllipsisWithTooltip",
          formatterType: "file",
          // file formatter renders an icon + filename HTML; export plain filename for excel
          _excelValueGetter: (row: IssueGridRow) => row.file || "",
          headerRenderer: fileHeaderRenderer,
        },
        {
          id: "fileVersionNumber",
          field: "fileVersionNumber",
          name: t("File Version"),
          originalWidth: 180,
          width: 180,
          filterable: false,
          clipMode: "EllipsisWithTooltip",
          headerRenderer: fileVersionHeaderRenderer,
        },
        {
          id: "assignedToUserName",
          field: "assignedTo",
          name: t("Assigned to"),
          originalWidth: 180,
          width: 180,
          filterable: true,
          filterType: "MultiSelect",
          formatterType: "userTagGroup",
          disableSorting: true,
          // clipMode: "EllipsisWithTooltip",
          _valueGetter: (row: IssueGridRow) => row.assignedTo?.map((u) => u.fullName) || [],
          _excelValueGetter: (row: IssueGridRow) => row.assignedTo?.map((u) => u.fullName).join(", ") || "",
          headerRenderer: assignedToHeaderRenderer,
        },
        {
          id: "officialComment",
          field: "officialComment",
          name: t("Official Response"),
          originalWidth: 180,
          width: 180,
          filterable: true,
          initiallyHidden: true,
          formatter: officialResponseFormatter,
          disableGrouping: true,
          disableFiltering: true,
          disableSorting: true,
          // Formatter extracts plain text from officialComment.title; mirror that for excel.
          _excelValueGetter: (row: IssueGridRow) => row.officialComment?.title || "-",
          headerRenderer: officialResponseHeaderRenderer,
        },
        {
          id: "createdByName",
          field: "createdByName",
          name: t("Created by"),
          originalWidth: 180,
          width: 180,
          filterable: true,
          filterType: "Text",
          formatterType: "userTag",
          // clipMode: "EllipsisWithTooltip",
          _valueGetter: (row: IssueGridRow) => row.createdBy,
          _filterValueGetter: (row: IssueGridRow) => row.createdByName || "",
          _excelValueGetter: (row: IssueGridRow) => row.createdByName || row.createdBy || "",
          headerRenderer: createdByHeaderRenderer,
        },
        {
          id: "createdDate",
          field: "createdDate",
          name: t("Created On"),
          originalWidth: 180,
          width: 180,
          filterable: true,
          filterType: "Date",
          formatterType: "Date",
          clipMode: "EllipsisWithTooltip",
          headerRenderer: createdOnHeaderRenderer,
          _excelValueGetter: (row: IssueGridRow) => convertDate(Number(row.createdDate), "dateForTooltip"),
        },
        {
          id: "dueDate",
          field: "dueDate",
          name: t("Due Date"),
          originalWidth: 180,
          width: 180,
          filterable: false,
          filterType: "Date",
          ...(isActionable
            ? {
                // SlickGrid formatter args: (rowIndex, cellIndex, cellValue, columnDef, dataContext).
                formatter: (_row: number, _cell: number, _value: unknown, _colDef: unknown, dataContext: IssueItem) =>
                  nextActionDueDateTemplate(dataContext),
              }
            : { formatterType: "Date" }),
          clipMode: "EllipsisWithTooltip",
          headerRenderer: dueDateHeaderRenderer,
          _excelValueGetter: (row: IssueItem) =>
            row.dueDate ? convertDate(Number(row.dueDate), "dateForTooltip") : "-",
        },
        {
          id: "severity",
          field: "severity",
          name: t("Severity"),
          originalWidth: 180,
          width: 180,
          filterType: "Text",
          formatter: createSeverityFormatter,
          _excelValueGetter: (row: IssueGridRow) => (row.severity ? severityTypes[row.severity] || row.severity : "-"),
          headerCssClass: "severity-header",
          headerRenderer: severityHeaderRenderer,
        },
        // The first assertion contextually types every literal above (so `filterType` stays the
        // narrow union rather than widening to `string`); the second drops the `null` the conditional
        // status column contributes and the filter then removes.
      ] as (IssueGridColumn | null)[]
    ).filter((col) => col && !col.hide) as IssueGridColumn[],
  );

  // Builds the request payload for the Take Action dashboard: the logged-in user's open issues across
  // their projects, bucketed by the active filter. Mirrors ReviewsList.buildBaseQuery.
  const buildActionableQuery = (): QueryShape => ({
    params: {},
    take: 10,
    requiresCounts: true,
    isActionable: true,
    assignedToUserId: userDetails?._id,
    ...(activeFilter === "overdue" && { overDue: true }),
    ...(activeFilter === "next7days" && { next7days: true }),
    ...(activeFilter === "next30days" && { next30days: true }),
    ...(selectedProjectIds?.length ? { selectedProjectIds } : {}),
  });

  const [queryParams, setQueryParams] = useState<QueryShape>(() =>
    isActionable
      ? buildActionableQuery()
      : {
          params: {
            ...(fileVersionIds?.length && { fileId: fileVersionIds }),
            ...(getStandaloneIssuesForSpaceId && { getStandaloneIssuesForSpaceId }),
            ...(getAllIssuesInSpace && { spaceId }),
            ...(projectId && { projectId }),
          },
          take: 10,
          ...(projectId && { projectId }),
        },
  );

  // Rebuild the actionable query whenever the dashboard filter/project selection changes.
  useEffect(() => {
    if (!isActionable) return;
    setQueryParams(buildActionableQuery());
  }, [isActionable, activeFilter, selectedProjectIds, userDetails?._id]);

  const handleExportToPdf = async () => {
    setShowExportPdfOptions(true);
  };

  const handleExportToPdfConfirm = (paperSize: PaperSize) => {
    setShowExportPdfOptions(false);
    // KNOWN BUG, preserved verbatim: `dataGrid.current` is dereferenced with no guard here, though
    // every sibling handler checks it. Erased assertion; a guard would change behaviour.
    const selectedRecords = dataGrid
      .current!.dataView.getAllSelectedItems()
      .filter((record: IssueGridRow) => record && !record.__isDummy);
    exportPdf({
      issuesIds: selectedRecords.map((record: IssueGridRow) => record.id),
      paperSize,
      exportType: EXPORT_TYPE.PDF,
    });

    // const res = await handleIssuesExportAsPDF(data);
    // if (res && !res.status) {
    //   dispatch(
    //     showToastNotification({
    //       content: untranslated(res.errorMessage),
    //       type: TOAST_TYPE_ERROR,
    //     }),
    //   );
    // }
  };

  const onClickLayout = (params: LayoutData) => {
    if (params.queryConditions) {
      if (params.queryConditions["createdByName"]) {
        setQueryParams((prev: QueryShape) => {
          const updatedParams = { ...prev };
          delete updatedParams.assignedToUserId;
          updatedParams.createdByuserId = userDetails?._id;
          return updatedParams;
        });
      } else {
        setQueryParams((prev: QueryShape) => {
          const updatedParams = { ...prev };
          delete updatedParams.createdByuserId;
          updatedParams.assignedToUserId = userDetails?._id;
          return updatedParams;
        });
      }
    } else {
      setQueryParams((prev: QueryShape) => {
        const updatedCondtion = { ...prev };
        delete updatedCondtion.createdByuserId;
        delete updatedCondtion.assignedToUserId;
        return updatedCondtion;
      });
    }
  };

  useEffect(() => {
    // `SlickDataView.items` is `protected` in the shipped slickgrid types even though the grid's own
    // callers read it; named here through an erased intersection over the ref's own dataView type.
    const items = (dataGrid.current?.dataView as DataViewWithItems | undefined)?.items;
    if (!items) return;

    const extraColumns: Record<string, IssueGridColumn> = {};

    // Extract column building logic into separate function
    const buildColumnFromField = (questionValue: string, fieldType: string, _id: string, sectionHeading: string) => {
      if (extraColumns[_id]) return; // Skip if already exists

      extraColumns[_id] = {
        field: `customFields.${_id}`,
        id: _id,
        width: 150,
        originalWidth: 150,
        textAlign: "Left",
        isCustomField: true,
        headerText: questionValue,
        _valueGetter: (args: IssueGridRow) => getCustomFieldValue(args, _id, fieldType),
        name: questionValue,
        columnGroup: sectionHeading,
        headerRenderer: () => renderToStaticMarkup(<HeaderWithIcon icon="Textbox" label={questionValue} />),
      };
    };

    // Extract value getter logic into separate function
    const getCustomFieldValue = (args: IssueGridRow, fieldId: string, fieldType: string) => {
      if (!args.formTemplate) return null;

      for (const templateItem of args.formTemplate) {
        const questionField = templateItem.questionFields?.find((q) => q._id === fieldId);
        if (questionField?.answer?.value) {
          const value = questionField.answer.value;
          return fieldType === "date-time" ? convertDate(Number(value), "dateForText") : value;
        }
      }
      return null;
    };

    // Process all rows and build extra columns
    for (const row of items) {
      if (!row.formTemplate) continue;

      for (const { questionFields, sectionHeading } of row.formTemplate) {
        if (!questionFields) continue;

        for (const { questionValue, fieldType, _id } of questionFields) {
          // `questionValue` is optional on the question schema; it is used as both the column
          // name and its header label, so the existing code assumes it is set. Erased assertion.
          buildColumnFromField(questionValue!, fieldType, _id, sectionHeading);
        }
      }
    }

    // Update column definitions
    setColumnDefinitions((prev: IssueGridColumn[]) => {
      const extraColumnIds = Object.keys(extraColumns);
      // A column `id` is `string | number`; widening the array keeps `includes` honest for the
      // numeric case rather than coercing the key.
      const existingColumns = prev.filter(
        (col: IssueGridColumn) => !(extraColumnIds as (string | number)[]).includes(col.id),
      );
      const newColumns = Object.values(extraColumns);
      return [...existingColumns, ...newColumns];
    });

    // Update grid options
    dataGrid.current?.slickGrid.setOptions({
      showPreHeaderPanel: Object.keys(extraColumns).length > 0,
    });
  }, [(dataGrid.current?.dataView as DataViewWithItems | undefined)?.items.length]);

  const handleRowSelection = () => {
    if (dataGrid.current) {
      const selectedRecords = dataGrid.current.dataView
        .getAllSelectedItems()
        .filter((record: IssueGridRow) => record && !record.__isDummy);
      setPdfExportEnabled(
        // KNOWN QUIRK, preserved verbatim: `length &&` yields the NUMBER `0` when nothing is
        // selected, so a number can land in this boolean state. Erased assertion; `!!` would emit.
        (selectedRecords.length &&
          !selectedRecords.some(
            (record: IssueGridRow) => !isIssueRequiredPermission(record, RESOURCE_DOWNLOAD.id),
          )) as boolean,
      );
      setSelectedIssues(selectedRecords);
      setSidebardata(selectedRecords);
      if (issueSelectionCallback) {
        issueSelectionCallback(selectedRecords);
      }
    }
  };

  return (
    <>
      <IssuesData
        showTitleLine={isActionable ? false : showTitleLine}
        issuesControlPaneClass={"flex w-full h-full"}
        ref={dataGrid}
        columnDefinitions={columnDefinitions}
        toolbarOptions={toolbarOptions1}
        remoteDataSource={getIssuesUrl}
        queryParams={queryParams}
        projectId={projectId}
        enableWrapText={enableWrapText}
        setEnableWrapText={setEnableWrapText}
        toolbarClickHandler={toolbarClickHandler1}
        onClickLayout={onClickLayout}
        onSelectedRowIdsChanged={handleRowSelection}
        openIssuesDetails={openIssuesDetails}
        closeIssueSidebar={closeIssueSidebar}
        sidebardata={sidebardata}
        disabledIssueIds={disabledIssueIds}
        disabledViews={isActionable ? true : disabledViews}
        allowOpenSidebar={isActionable ? false : allowOpenSidebar}
        // Keep the grid tools (filter/sort/column-chooser/row-resize/grouping/excel export) visible in
        // dashboard mode. Create Issue + PDF export are already excluded here (they require a route projectId).
        allowTools={allowTools}
        outerClassName={!isActionable && allowOpenSidebar ? "pe-4" : ""}
        refreshDataGrid={refreshDataGrid}
        updateGridRowData={updateGridRowData}
        onClick={isActionable ? recordClick : undefined}
      />
      {showExportPdfOptions && (
        <ExportPdfOptionsModal
          isVisible
          onExport={handleExportToPdfConfirm}
          onCancel={() => setShowExportPdfOptions(false)}
        />
      )}
      {(openCreateIssue || editIssue) && (
        <CreateIssueModal
          key={editIssue ? "edit" : "create"}
          closeModal={handleModalClose}
          refreshDataGrid={refreshDataGrid}
          projectId={projectId}
          attachedReviewId={null}
          defaultSpaceId={editIssue ? (editIssue.spaceId ?? null) : spaceId || null}
          extraParametersOnIssueCreation={{}}
          isEditMode={Boolean(editIssue)}
        />
      )}
      {showDeleteConfirm && (
        <ModalStateDialogComponent
          isVisible
          width="27.5rem"
          onCancel={() => setShowDeleteConfirm(false)}
          headerTitle={<Text>{t("Delete Issue(s)")}</Text>}
          modelContent={
            <Text sx={{ whiteSpace: "normal" }}>{t("Are you sure you want to delete selected Issue(s)?")}</Text>
          }
          footerContent={
            <div className="flex w-full gap-4">
              <ButtonComponent
                buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
                onClick={handleConfirmDelete}
                loading={isDeleting}
              >
                <Text>{t("Confirm")}</Text>
              </ButtonComponent>
              <ButtonComponent
                buttonType={BUTTON_TYPES.BORDER_BUTTON}
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                <Text>{t("Cancel")}</Text>
              </ButtonComponent>
            </div>
          }
        />
      )}
    </>
  );
}

Issues.displayName = "Issues";

export default Issues;
